import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import PhoneFrame from "@/components/landing/PhoneFrame";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Faq from "@/components/landing/Faq";
import { track } from "@/lib/tracking";

const CTAButton = ({ cta, primary = true, testId }) => {
  if (!cta) return null;
  const className = primary
    ? "group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--forest))] px-7 py-4 text-sm font-medium text-[hsl(var(--background))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))]"
    : "inline-flex items-center gap-2 rounded-full border border-[hsl(var(--ink))] px-7 py-4 text-sm font-medium text-[hsl(var(--ink))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]";

  const isInternal = cta.href.startsWith("/") && !cta.href.startsWith("/#");
  if (isInternal) {
    return (
      <Link to={cta.href} data-testid={testId} className={className}>
        {cta.label}
        {primary && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.8} />}
      </Link>
    );
  }
  return (
    <a href={cta.href} data-testid={testId} className={className}>
      {cta.label}
      {primary && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.8} />}
    </a>
  );
};

export const SubLanding = ({ config, seo, path, breadcrumbs }) => {
  if (!config) return null;
  const features = config.features ?? config.benefits ?? [];
  const tiers = config.pricing?.tiers ?? [];

  return (
    <main data-testid={`landing-${config.slug}`} className="bg-background">
      {seo && (
        <Seo
          {...seo}
          path={path ?? `/${config.slug}`}
          breadcrumbs={breadcrumbs}
          faq={config.faq}
        />
      )}
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}

      {/* Hero */}
      <section
        data-testid="subhero"
        className="relative overflow-hidden bg-background pt-16 lg:pt-24"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-20 lg:grid-cols-12 lg:px-10 lg:pb-28">
          <div className={config.heroScreenshot ? "lg:col-span-7" : "lg:col-span-8"}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[hsl(var(--ink-soft))]">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--sand))]" strokeWidth={1.8} />
              {config.eyebrow}
            </div>
            <h1 className="mt-8 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-6xl lg:text-[5rem]">
              {config.title}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[hsl(var(--ink-soft))] sm:text-xl">
              {config.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <CTAButton cta={config.primaryCTA} primary testId="sub-cta-primary" trackLabel={`sub_primary_${config.slug}`} />
              <CTAButton cta={config.secondaryCTA} primary={false} testId="sub-cta-secondary" trackLabel={`sub_secondary_${config.slug}`} />
            </div>
            <p className="mt-4 text-sm text-[hsl(var(--sand-dark))]">
              <Link to="/" className="underline-offset-4 hover:underline">
                ← Terug naar overzicht
              </Link>
            </p>
          </div>

          {config.heroScreenshot && (
            <div className="relative lg:col-span-5">
              <div className="absolute -left-10 top-0 h-72 w-72 rounded-full bg-[hsl(var(--sand))]/15 blur-3xl" />
              <div className="absolute -bottom-10 right-0 h-72 w-72 rounded-full bg-[hsl(var(--forest))]/10 blur-3xl" />
              <div className="relative">
                {config.heroScreenshot.embeddedFrame ? (
                  <img
                    src={config.heroScreenshot.url}
                    alt={config.heroScreenshot.alt}
                    data-testid={`sub-hero-phone-${config.slug}`}
                    className="relative z-10 mx-auto h-auto w-full max-w-[340px] drop-shadow-[0_30px_60px_rgba(26,37,30,0.22)]"
                  />
                ) : (
                  <PhoneFrame
                    testId={`sub-hero-phone-${config.slug}`}
                    src={config.heroScreenshot.url}
                    alt={config.heroScreenshot.alt}
                    className="!max-w-[300px]"
                  />
                )}
                {config.heroScreenshot.caption && (
                  <p className="mt-6 text-center text-sm text-[hsl(var(--ink-soft))]">
                    {config.heroScreenshot.caption}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Problem + Solution */}
      {(config.problem || config.solution) && (
        <section className="bg-[hsl(var(--surface-secondary))] py-20 lg:py-28">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
            {config.problem && (
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">
                  Het probleem
                </p>
                <p className="mt-5 font-display text-2xl leading-snug text-[hsl(var(--ink))] sm:text-3xl">
                  {config.problem}
                </p>
              </div>
            )}
            {config.solution && (
              <div className="rounded-3xl border border-[hsl(var(--forest))]/15 bg-[hsl(var(--surface))] p-8 sm:p-12">
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--forest))]">
                  Met EquiSight
                </p>
                <p className="mt-5 font-display text-2xl leading-snug text-[hsl(var(--ink))] sm:text-3xl">
                  {config.solution}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Audience */}
      {config.audience && (
        <section className="bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              Voor wie
            </p>
            <p className="mt-5 font-display text-2xl text-[hsl(var(--ink))] sm:text-3xl">
              {config.audience}
            </p>
          </div>
        </section>
      )}

      {/* Scenario block */}
      {config.scenario && (
        <section className="bg-[hsl(var(--ink))] py-20 text-[hsl(var(--background))] lg:py-28">
          <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
            <p className="text-xs uppercase tracking-[0.22em] text-red-300">
              Scenario
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.1] text-balance sm:text-5xl lg:text-6xl">
              {config.scenario.title}
            </h2>
            <ol className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-4 text-left sm:grid-cols-3">
              {config.scenario.steps.map((s, i) => (
                <li
                  key={s}
                  className="rounded-2xl border border-white/10 bg-white/5 p-5"
                >
                  <span className="font-display text-3xl text-[hsl(var(--sand))]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-2 text-[hsl(var(--background))]/85">{s}</p>
                </li>
              ))}
            </ol>
            {config.scenario.tagline && (
              <p className="mt-10 font-display text-2xl text-[hsl(var(--sand))] sm:text-3xl">
                {config.scenario.tagline}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Features */}
      {features.length > 0 && (
        <section id="features" className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                {config.features ? "Functionaliteiten" : "Voordelen"}
              </p>
              <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
                Wat je krijgt.
              </h2>
            </div>
            <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 text-[hsl(var(--ink))]"
                >
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[hsl(var(--forest))] text-[hsl(var(--background))]">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Benefits when separate from features */}
      {config.features && config.benefits && config.benefits.length > 0 && (
        <section className="bg-[hsl(var(--surface-secondary))] py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              Waarde
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
              Wat het oplevert.
            </h2>
            <ul className="mt-10 space-y-4">
              {config.benefits.map((b) => (
                <li
                  key={b}
                  className="flex items-center gap-4 border-b border-[hsl(var(--border))] pb-4 text-lg text-[hsl(var(--ink))]"
                >
                  <span className="font-display text-xl text-[hsl(var(--sand-dark))]">→</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Showcase split (optional) */}
      {config.showcase && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
              <div className="lg:col-span-7">
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                  {config.showcase.eyebrow}
                </p>
                <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
                  {config.showcase.title}
                </h2>
                <p className="mt-6 max-w-xl text-[hsl(var(--ink-soft))]">
                  {config.showcase.description}
                </p>
                {config.showcase.bullets && (
                  <ul className="mt-8 space-y-3">
                    {config.showcase.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[hsl(var(--ink))]"
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--sand-dark))]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {config.showcase.image && (
                <div className="relative lg:col-span-5">
                  <div className="absolute -left-8 -top-8 h-64 w-64 rounded-full bg-[hsl(var(--sand))]/15 blur-3xl" />
                  {config.showcase.image.embeddedFrame ? (
                    <img
                      src={config.showcase.image.url}
                      alt={config.showcase.image.alt}
                      data-testid={`sub-showcase-phone-${config.slug}`}
                      className="relative z-10 mx-auto h-auto w-full max-w-[320px] drop-shadow-[0_30px_60px_rgba(26,37,30,0.22)]"
                    />
                  ) : (
                    <PhoneFrame
                      src={config.showcase.image.url}
                      alt={config.showcase.image.alt}
                      className="!max-w-[300px]"
                      testId={`sub-showcase-phone-${config.slug}`}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      {tiers.length > 0 && (
        <section id="pricing" className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              Prijzen
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
              Eerlijk en transparant.
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {tiers.map((t) => (
                <article
                  key={t.name}
                  data-testid={`sub-tier-${t.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`rounded-3xl border p-7 ${
                    t.highlight
                      ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-[hsl(var(--background))]"
                      : "border-[hsl(var(--border))] bg-[hsl(var(--surface))]"
                  }`}
                >
                  <p
                    className={`text-xs uppercase tracking-[0.22em] ${
                      t.highlight ? "text-[hsl(var(--background))]/65" : "text-[hsl(var(--ink-soft))]"
                    }`}
                  >
                    {t.name}
                  </p>
                  <p className="mt-3 font-display text-5xl font-semibold tracking-tight">
                    {t.price}
                  </p>
                  <p
                    className={`mt-3 text-sm ${
                      t.highlight ? "text-[hsl(var(--background))]/75" : "text-[hsl(var(--ink-soft))]"
                    }`}
                  >
                    {t.note}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Checklist (conversion tool) */}
      {config.checklist && (
        <section className="bg-[hsl(var(--surface-secondary))] py-20 lg:py-28">
          <div className="mx-auto max-w-3xl px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.22em] text-red-600">
              Doe het nu
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
              {config.checklist.title}
            </h2>
            <ul className="mt-10 space-y-3">
              {config.checklist.items.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5"
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span className="text-[hsl(var(--ink))]">{item}</span>
                </li>
              ))}
            </ul>
            {config.checklist.cta && (
              <div className="mt-10">
                <a
                  href={config.checklist.cta.href}
                  data-testid="checklist-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-red-700"
                >
                  {config.checklist.cta.label}
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Microcopy trust line */}
      {config.microcopy && config.microcopy.length > 0 && (
        <section className="bg-background py-10">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))] lg:px-10">
            {config.microcopy.map((m) => (
              <span key={m} className="inline-flex items-center gap-2">
                <Check className="h-3 w-3 text-[hsl(var(--forest))]" strokeWidth={2.5} />
                {m}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {config.faq && config.faq.length > 0 && (
        <Faq items={config.faq} eyebrow="FAQ" title="Veelgestelde vragen" />
      )}

      {/* Final CTA for the sub-landing (uses the global waitlist form) */}
      <FinalCTA />

      <Footer />
    </main>
  );
};

export default SubLanding;
